/**
 * Garage Discover View
 * Main discovery page with sales stage filtering and tool cards
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Search, Plus, X, Heart, ExternalLink, Star, TrendingUp, Grid, List, Table, Bookmark, Send, Flame, Settings, Sparkles, ChevronDown, ChevronUp, Share2, Check } from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import FacetDropdown from '../components/FacetDropdown';
import GarageMatrixView from '../components/GarageMatrixView';
import GarageToolDetailEnhanced from './GarageToolDetailEnhanced';
import GarageLoadingScreen from '../components/GarageLoadingScreen';
import TourGuide from '../components/TourGuide';
import { getDiscoverTourSteps } from '../tours/discoverTour';
import AlertModal from '../modals/AlertModal';
import ShareToolModal from '../modals/ShareToolModal';
import RequestAccessModal from '../modals/RequestAccessModal';
import ToolAccessPrimaryButton, {
  getToolAccessButtonState,
} from '../components/ToolAccessPrimaryButton';
import ToolCardActions from '../components/ToolCardActions';
import ToolCardDescriptionOrSteps from '../components/ToolCardDescriptionOrSteps';
import { getToolCardClickAction, getToolTypeBadgeSuffix } from '../../../utils/toolAppSubtype';
import config from '../../../config/environment';
import { getVisibleDiscoverFilters } from '../utils/discoverFilters';
import { toolMatchesDefaultAccess } from '../utils/discoverAccess';

// Predefined filter options (shown first in dropdowns)
const PREDEFINED_JOURNEYS = ['Prospect', 'Qualify', 'Demo/Eval', 'Proposal', 'Onboard', 'Expand', 'Adopt', 'Retain', 'Backstage'];
const PREDEFINED_ROLES = ['AE', 'SE', 'CSM', 'TAM', 'BDR', 'Channel Manager', 'Leadership'];
const PREDEFINED_TAGS = ['Sell', 'Support', 'Organization', 'Productivity', 'Communication', 'RingEX', 'RingSense', 'AI WFM'];
const PREDEFINED_CAPABILITIES = ['research', 'generate', 'analyze', 'calculate', 'automate', 'visualize'];
const PREDEFINED_PERSONAS = ['Sales', 'Customer Success', 'Product', 'IT', 'Engineering', 'Marketing', 'Finance', 'Support', 'Operations', 'HR'];

const GROUPING_OPTIONS = [
  { value: 'none', label: 'No grouping' },
  { value: 'journey', label: 'By sales stage' },
  { value: 'role', label: 'By role' },
  { value: 'type', label: 'By type (Apps vs Gems)' }
];

const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'recent', label: 'Recently used' },
  { value: 'popular', label: 'Most used' },
  { value: 'new', label: 'Newest' },
  { value: 'az', label: 'A → Z' }
];

const GarageDiscoverView = ({
  userEmail,
  userName,
  isAdmin,
  userRole,
  accessPersona,
  accessPersonas = [],
  userProfile,
  onNavigate,
  onOpenTool,
  onToolLaunch,
  onNavigateToMaker,
  initialToolId, // New prop for cross-view navigation
  setDetailViewState, // For controlling header back button
  onRefreshPinnedTools, // Callback to refresh sidebar pinned tools
  configVersion, // Config version for auto-refresh when admin changes config
  showOnboarding = false,
  onboardingChecked = true
}) => {
  const [tools, setTools] = useState([]);
  const [usageHistory, setUsageHistory] = useState({ email: null, ids: [], loaded: false });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeJourneys, setActiveJourneys] = useState([]);
  const [activeRoles, setActiveRoles] = useState([]);
  const [activeTags, setActiveTags] = useState([]);
  const [activeCapabilities, setActiveCapabilities] = useState([]);
  const [activePersonas, setActivePersonas] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [pins, setPins] = useState([]);
  const [sharedToolId, setSharedToolId] = useState(null); // For share success feedback
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [toolToShare, setToolToShare] = useState(null);
  // Start false so tour never overlaps onboarding; enabled after onboarding finishes
  const [showTour, setShowTour] = useState(false);

  // Dynamic filter options (fetched from config API)
  const [journeyOptions, setJourneyOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [capabilityOptions, setCapabilityOptions] = useState([]);
  const [personaOptions, setPersonaOptions] = useState([]);
  const [departmentConfigs, setDepartmentConfigs] = useState([]);
  const [personaConfigs, setPersonaConfigs] = useState([]);
  const [teamConfigs, setTeamConfigs] = useState([]);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list', 'matrix'
  const [gridSize, setGridSize] = useState(() => {
    return localStorage.getItem('garageGridSize') || 'medium';
  }); // 'small', 'medium', 'large'
  const [groupBy, setGroupBy] = useState('none');
  const [sortBy, setSortBy] = useState('recommended');
  const [toolType, setToolType] = useState('all'); // 'all', 'App', 'Gem'
  const [collapsedSections, setCollapsedSections] = useState(new Set(['recently-used', 'recommended', 'try-new', 'bookmarked'])); // Track collapsed sections - pill sections collapsed by default
  const [filterMode, setFilterMode] = useState(() => {
    // Load saved preference from localStorage
    return localStorage.getItem('garageFilterMode') || 'simplified';
  }); // 'simplified' or 'advanced'

  // Detail view state
  const [selectedTool, setSelectedTool] = useState(null);
  const [toolDetails, setToolDetails] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewFormData, setReviewFormData] = useState({
    rating: 0,
    text: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  // Alert modal state
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info'
  });

  // Access request (Phase 1) — server-evaluated button states keyed by tool id
  const [accessStates, setAccessStates] = useState({});
  const [requestAccessTool, setRequestAccessTool] = useState(null);

  // User preference state
  const [preferencesApplied, setPreferencesApplied] = useState(false);
  const [preferenceSource, setPreferenceSource] = useState(null); // 'onboarding' | null
  const [accessDepartment, setAccessDepartment] = useState(null);
  const [profileTeam, setProfileTeam] = useState(null);
  const [grantedToolIds, setGrantedToolIds] = useState([]);
  const visibleDiscoverFilters = useMemo(
    () =>
      getVisibleDiscoverFilters(
        accessDepartment,
        departmentConfigs,
        personaConfigs,
        userRole,
        accessPersona,
        profileTeam,
        teamConfigs,
        accessPersonas
      ),
    [
      accessDepartment,
      departmentConfigs,
      personaConfigs,
      userRole,
      accessPersona,
      accessPersonas,
      profileTeam,
      teamConfigs
    ]
  );
  const isFilterVisible = (facet) => visibleDiscoverFilters.includes(facet);
  const discoverTourSteps = useMemo(() => getDiscoverTourSteps(filterMode), [filterMode]);

  // Start Discover tour only after onboarding is checked and finished/skipped
  useEffect(() => {
    if (!onboardingChecked || showOnboarding) {
      setShowTour(false);
      return;
    }
    const tourDone = localStorage.getItem('tour-completed-discover') === 'true';
    setShowTour(!tourDone);
  }, [onboardingChecked, showOnboarding]);

  // Helper function to map hub organization to groupBy
  const mapHubOrgToGroupBy = (hubOrg) => {
    const orgMap = {
      'by_sales_stage': 'journey',
      'by_role': 'role',
      'by_capability': 'none', // Capabilities not in grouping options yet
      'no_grouping': 'none'
    };
    return orgMap[hubOrg] || 'none';
  };

  // Apply user preferences on initial load
  useEffect(() => {
    const applyUserPreferences = async () => {
      // Check if user has disabled auto-apply preferences
      const autoApplyDisabled = localStorage.getItem('garageDisableAutoPreferences') === 'true';
      if (autoApplyDisabled) {
        console.log('🚫 Auto-apply preferences disabled by user');
        return;
      }

      // Check if user manually cleared filters this session
      const sessionCleared = sessionStorage.getItem('garagePreferencesCleared');
      if (sessionCleared) {
        console.log('ℹ️ User cleared preferences this session, not re-applying');
        return;
      }

      try {
        console.log('🔍 Fetching user profile for preference-based filtering...');
        const profile = await garageAPI.getUserProfile(userEmail);

        if (profile && profile.onboardingStatus === 'completed') {
          console.log('✅ User profile found:', profile);
          setAccessDepartment(profile.department || null);
          // The Settings/Onboarding team selection is stored on profile.persona.
          setProfileTeam(profile.persona || null);
          setGrantedToolIds(Array.isArray(profile.grantedToolIds) ? profile.grantedToolIds : []);

          // Profile preferences influence grouping/recommendations, but must not
          // silently remove published tools from Discover. Users can still opt
          // into role, journey, and persona filters from the filter controls.

          // Apply groupBy based on hub organization preference
          if (profile.preferences?.hub_organization) {
            const mappedGroupBy = mapHubOrgToGroupBy(profile.preferences.hub_organization);
            setGroupBy(mappedGroupBy);
            console.log(`🎯 Applied groupBy: ${mappedGroupBy}`);
          }

          setPreferencesApplied(false);
          setPreferenceSource(null);
        } else {
          console.log('ℹ️ No completed user profile found, showing all tools');
        }
      } catch (error) {
        console.log('ℹ️ No user preferences found, showing all tools:', error.message);
      }
    };

    if (userEmail && userEmail !== 'unknown') {
      applyUserPreferences();
    }
  }, [userEmail]); // Only run once when userEmail is available

  // Keep department in sync when parent refreshes profile.
  useEffect(() => {
    if (!userProfile) return;
    setAccessDepartment(userProfile.department || null);
    setProfileTeam(userProfile.persona || null);
    setGrantedToolIds(Array.isArray(userProfile.grantedToolIds) ? userProfile.grantedToolIds : []);
  }, [userProfile]);

  // A hidden facet must never keep filtering the catalog invisibly.
  useEffect(() => {
    if (filterOptionsLoading) return;
    const visible = new Set(visibleDiscoverFilters);
    if (!visible.has('journey')) setActiveJourneys([]);
    if (!visible.has('role')) setActiveRoles([]);
    if (!visible.has('tag')) setActiveTags([]);
    if (!visible.has('capability')) setActiveCapabilities([]);
    if (!visible.has('persona')) setActivePersonas([]);
  }, [filterOptionsLoading, visibleDiscoverFilters]);

  // Load filter options on mount and when config changes
  useEffect(() => {
    loadDynamicFilterOptions();
  }, [configVersion]); // Reload when admin changes config options

  // Load full published catalog once; search/filters are client-side (instant, no stale subsets)
  useEffect(() => {
    loadTools();
    loadFavorites();
    loadPins();
  }, []);

  useEffect(() => {
    if (selectedTool) {
      loadToolDetails(selectedTool);
    }
  }, [selectedTool]);

  useEffect(() => {
    let cancelled = false;
    setUsageHistory({ email: userEmail, ids: [], loaded: false });
    if (userEmail) {
      garageAPI.getUserUsedToolIds(userEmail).then((ids) => {
        if (!cancelled) {
          setUsageHistory((current) => ({
            email: userEmail,
            ids: [...new Set([...current.ids, ...ids])],
            loaded: true,
          }));
        }
      }).catch((error) => {
        console.warn('Could not load usage history for new-tool suggestions:', error);
      });
    }
    return () => { cancelled = true; };
  }, [userEmail]);

  // Handle cross-view navigation: auto-open tool if initialToolId is provided
  // Also close tool detail when navigating away (initialToolId becomes null/undefined)
  useEffect(() => {
    if (initialToolId && initialToolId !== selectedTool) {
      // Open tool detail when initialToolId is provided
      handleShowDetails(initialToolId);
    } else if (!initialToolId && selectedTool !== null) {
      // Close tool detail when navigating back to list view
      // Handles both null and undefined values
      setSelectedTool(null);
      setToolDetails(null);
      setReviews([]);
      setShowReviewForm(false);
      setReviewFormData({ rating: 0, text: '' });
      // Clear header back button
      if (setDetailViewState) {
        setDetailViewState({ isDetailView: false, backLabel: '', onBack: null });
      }
    }
  }, [initialToolId]);

  // Save filter mode preference
  useEffect(() => {
    localStorage.setItem('garageFilterMode', filterMode);
  }, [filterMode]);

  // Save grid size preference
  useEffect(() => {
    localStorage.setItem('garageGridSize', gridSize);
  }, [gridSize]);

  // Load dynamic filter options from config API
  const loadDynamicFilterOptions = async () => {
    try {
      setFilterOptionsLoading(true);
      const [
        journeys,
        roles,
        tags,
        capabilities,
        personas,
        departments,
        teams
      ] = await Promise.all([
        garageAPI.getConfigOptions('journey'),
        garageAPI.getConfigOptions('tool_target_role'),
        garageAPI.getConfigOptions('tag'),
        garageAPI.getConfigOptions('capability'),
        garageAPI.getConfigOptions('persona'),
        garageAPI.getConfigOptions('department'),
        garageAPI.getConfigOptions('team')
      ]);

      // Map to labels for filter display. Role is the one category where
      // value !== label (e.g. value "BDR", label "BDR / SDR") — tool.roles
      // stores the value, so the role facet must filter/match by value and
      // only use label for display, unlike the other facets below.
      setJourneyOptions(journeys.map(j => j.label));
      setRoleOptions(roles.map(r => ({ value: r.value, label: r.label || r.value })));
      setTagOptions(tags.map(t => t.label));
      setCapabilityOptions(capabilities.map(c => c.label));
      setPersonaOptions(personas.map(p => p.label));
      setPersonaConfigs(personas);
      setDepartmentConfigs(departments);
      setTeamConfigs(teams);

      console.log('✅ Loaded dynamic filter options:', {
        journeys: journeys.length,
        roles: roles.length,
        tags: tags.length,
        capabilities: capabilities.length,
        personas: personas.length,
        teams: teams.length
      });
    } catch (error) {
      console.error('❌ Failed to load filter options from config API:', error);
      // Fallback to predefined options if API fails
      console.log('⚠️ Using predefined filter options as fallback');
      setJourneyOptions(PREDEFINED_JOURNEYS);
      setRoleOptions(PREDEFINED_ROLES.map(r => ({ value: r, label: r })));
      setTagOptions(PREDEFINED_TAGS);
      setCapabilityOptions(PREDEFINED_CAPABILITIES);
      setPersonaOptions(PREDEFINED_PERSONAS);
    } finally {
      setFilterOptionsLoading(false);
    }
  };

  const loadTools = async () => {
    try {
      setLoading(true);
      // Always load the full published set; search is applied client-side
      const data = await garageAPI.getTools({
        status: 'published'
      });
      console.log('🔍 Discover View - Loaded tools:', data);
      // Log Legal Translator specifically for debugging
      const legalTool = data.find(t => t.title === 'Legal Translator');
      if (legalTool) {
        console.log('🔍 Discover - Legal Translator tool data:', {
          makerEmail: legalTool.makerEmail,
          makerName: legalTool.makerName,
          makers: legalTool.makers,
          createdBy: legalTool.createdBy,
          created_by_email: legalTool.created_by_email,
          createdByEmail: legalTool.createdByEmail
        });
      }
      setTools(data);
      // Note: Filter options are now loaded from config API, not computed from tools
      if (config.ACCESS_REQUESTS_ENABLED && userEmail && data?.length) {
        try {
          const ids = data.map((t) => t.id).filter(Boolean);
          const stateResp = await garageAPI.getAccessState({
            resource_type: 'garage_tool',
            external_refs: ids.join(','),
          });
          setAccessStates(stateResp?.states || {});
        } catch (accessErr) {
          console.warn('Access state unavailable; defaulting to Launch', accessErr);
          setAccessStates({});
        }
      } else {
        setAccessStates({});
      }
    } catch (error) {
      console.error('Failed to load tools:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const favs = await garageAPI.getUserFavorites(userEmail);
      setFavorites(favs);
    } catch (error) {
      console.error('Failed to load favorites:', error);
    }
  };

  const loadPins = async () => {
    try {
      const userPins = await garageAPI.getUserPins(userEmail);
      setPins(userPins.map(pin => pin.toolId));
    } catch (error) {
      console.error('Failed to load pins:', error);
    }
  };

  const loadToolDetails = async (toolId) => {
    try {
      setDetailLoading(true);
      const data = await garageAPI.getTool(toolId);
      setToolDetails(data);
      await loadReviews(toolId);

      // Set header back button
      if (setDetailViewState) {
        setDetailViewState({
          isDetailView: true,
          backLabel: 'Back to Tools',
          onBack: closeToolDetail
        });
      }
    } catch (error) {
      console.error('Failed to load tool details:', error);
      setToolDetails(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const loadReviews = async (toolId) => {
    try {
      const data = await garageAPI.getReviews(toolId);
      setReviews(data);
    } catch (error) {
      console.error('Failed to load reviews:', error);
      setReviews([]);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (reviewFormData.rating === 0) {
      setAlertModal({
        isOpen: true,
        title: 'Rating Required',
        message: 'Please select a rating before submitting your review.',
        variant: 'warning'
      });
      return;
    }

    try {
      setSubmittingReview(true);
      await garageAPI.createReview({
        toolId: selectedTool,
        userEmail: userEmail,
        userName: userName || userEmail?.split('@')[0],
        rating: reviewFormData.rating,
        text: reviewFormData.text
      });

      setReviewFormData({ rating: 0, text: '' });
      setShowReviewForm(false);
      await loadReviews(selectedTool);
      setAlertModal({
        isOpen: true,
        title: 'Success',
        message: 'Review submitted successfully!',
        variant: 'success'
      });
    } catch (error) {
      console.error('Failed to submit review:', error);
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to submit review. Please try again.',
        variant: 'error'
      });
    } finally {
      setSubmittingReview(false);
    }
  };

  // Open tool details modal
  const handleShowDetails = (toolId) => {
    // Only update if we're switching to a different tool
    if (toolId !== selectedTool) {
      setSelectedTool(toolId);
      setActiveTab('overview');

      // Update URL to reflect tool detail view
      // Use window.history.pushState to avoid re-rendering the entire view
      const newUrl = `/ai-garage/discover/${toolId}`;
      if (window.location.pathname !== newUrl) {
        window.history.pushState({ view: 'discover', toolId }, '', newUrl);
      }
    }
  };

  // Direct launch tool
  const handleToolClick = (tool) => {
    getToolCardClickAction(tool, { onOpenTool, handleLaunchTool })();
  };

  const closeToolDetail = () => {
    setSelectedTool(null);
    setToolDetails(null);
    setReviews([]);
    setShowReviewForm(false);
    setReviewFormData({ rating: 0, text: '' });
    // Clear header back button
    if (setDetailViewState) {
      setDetailViewState({ isDetailView: false, backLabel: '', onBack: null });
    }
    // Clear URL by navigating back to discover without toolId
    if (onNavigate) {
      onNavigate('discover');
    }
  };

  const toggleFacet = (facet, value) => {
    const setters = {
      journey: setActiveJourneys,
      role: setActiveRoles,
      tag: setActiveTags,
      capability: setActiveCapabilities,
      persona: setActivePersonas
    };

    const getters = {
      journey: activeJourneys,
      role: activeRoles,
      tag: activeTags,
      capability: activeCapabilities,
      persona: activePersonas
    };

    const setter = setters[facet];
    const current = getters[facet];

    if (current.includes(value)) {
      setter(current.filter(v => v !== value));
    } else {
      setter([...current, value]);
    }
  };

  const clearAllFilters = () => {
    setActiveJourneys([]);
    setActiveRoles([]);
    setActiveTags([]);
    setActiveCapabilities([]);
    setActivePersonas([]);
    setSearchQuery('');
    setPreferencesApplied(false);
    setPreferenceSource(null);
    // Prevent profile preferences from being re-applied during this session.
    sessionStorage.setItem('garagePreferencesCleared', 'true');
  };

  // Full admin keeps the full catalog when Mode A filters are cleared.
  // Everyone else enters Mode B: department match OR explicit tool grant.
  const isFullAdmin = userRole === 'admin';
  const hasModeAFilters =
    activeJourneys.length > 0 ||
    activeRoles.length > 0 ||
    activePersonas.length > 0;
  const hasCatalogAccessScope = Boolean((accessDepartment || '').trim())
    || accessPersonas.length > 0
    || Boolean((accessPersona || '').trim())
    || grantedToolIds.length > 0;
  const isModeB = config.ACCESS_REQUESTS_ENABLED
    && userRole !== null
    && !isFullAdmin
    && !hasModeAFilters
    && hasCatalogAccessScope;

  const removeFilter = (facet, value) => {
    toggleFacet(facet, value);
  };

  const handleMatrixCellClick = (toolId) => {
    // Open tool detail when clicking on a tool chip in matrix
    if (onOpenTool) {
      onOpenTool(toolId);
    }
  };

  const toggleFavorite = async (toolId) => {
    const isFavorited = favorites.includes(toolId);
    try {
      if (isFavorited) {
        await garageAPI.removeFavorite(toolId, userEmail);
        setFavorites(favorites.filter(id => id !== toolId));
      } else {
        await garageAPI.addFavorite({ toolId, userEmail });
        setFavorites([...favorites, toolId]);
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const togglePin = async (toolId) => {
    const isPinned = pins.includes(toolId);
    try {
      if (isPinned) {
        await garageAPI.removePin(toolId, userEmail);
        setPins(pins.filter(id => id !== toolId));
      } else {
        await garageAPI.addPin({ toolId, userEmail });
        setPins([...pins, toolId]);
      }
      // Refresh sidebar pinned tools immediately
      if (onRefreshPinnedTools) {
        onRefreshPinnedTools();
      }
    } catch (error) {
      console.error('Failed to toggle pin:', error);
    }
  };

  const handleLaunchTool = async (tool) => {
    // Remove launched tools immediately, including while saved history is loading.
    setUsageHistory((current) => ({
      email: userEmail,
      ids: [...new Set([...(current.email === userEmail ? current.ids : []), tool.id])],
      loaded: current.email === userEmail && current.loaded,
    }));
    // Track recently used tools
    try {
      const recentToolIds = JSON.parse(localStorage.getItem('garage-recent-tools') || '[]');
      const updatedRecent = [tool.id, ...recentToolIds.filter(id => id !== tool.id)].slice(0, 10);
      localStorage.setItem('garage-recent-tools', JSON.stringify(updatedRecent));
    } catch (error) {
      console.error('Failed to update recent tools:', error);
    }

    // Use parent component's launch handler if provided
    if (onToolLaunch) {
      onToolLaunch(tool);
    } else if (tool?.url) {
      // Fallback to simple launch
      window.open(tool.url, '_blank');
    }
  };

  const handleCardPrimaryAction = (tool) => {
    const state = getToolAccessButtonState(accessStates, tool.id);
    if (state === 'pending') return;
    if (state === 'request' || state === 'rerequest') {
      setRequestAccessTool(tool);
      return;
    }
    getToolCardClickAction(tool, { onOpenTool, handleLaunchTool })();
  };

  const handleSubmitAccessRequest = async (tool) => {
    const created = await garageAPI.createAccessRequest({
      resourceType: 'garage_tool',
      externalRef: tool.id,
    });
    setAccessStates((prev) => ({
      ...prev,
      [tool.id]: {
        buttonState: 'pending',
        resourceId: created.resourceId,
        externalRef: tool.id,
        resourceType: 'garage_tool',
        rejectionReason: null,
        requestId: created.id,
      },
    }));
    setRequestAccessTool(null);
    setAlertModal({
      isOpen: true,
      title: 'Request submitted',
      message: `Access requested for ${tool.title}. You can track it under Access requests.`,
      variant: 'info',
    });
  };

  const handleShareTool = (e, tool) => {
    e.stopPropagation();
    // Open the share modal with advanced options
    setToolToShare(tool);
    setShareModalOpen(true);
  };

  const filteredTools = tools.filter(tool => {
    // The API already removes specific-user Apps for non-members. Default
    // discovery then applies department/grant rules without hiding authorized
    // audience members or legacy tools that have no department configuration.
    if (
      isModeB &&
      !toolMatchesDefaultAccess(
        tool,
        accessDepartment,
        accessPersonas.length ? accessPersonas : accessPersona,
        grantedToolIds
      )
    ) {
      return false;
    }

    // Client-side search across title, description, makers, tags, roles, journeys, etc.
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      const makerEmails = (tool.makers || [])
        .map((m) => `${m.makerEmail || ''} ${m.email || ''} ${m.makerName || ''} ${m.displayName || ''}`)
        .join(' ');
      const haystack = [
        tool.title,
        tool.description,
        tool.toolType,
        tool.makerEmail,
        tool.makerName,
        tool.createdBy,
        tool.createdByEmail,
        tool.createdByName,
        makerEmails,
        ...(tool.tags || []),
        ...(tool.roles || []),
        ...(tool.journeys || []),
        ...(tool.capabilities || []),
        ...(tool.personas || []),
        ...(tool.departments || [])
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      // Every search word must appear somewhere (order-independent)
      const words = q.split(/\s+/).filter(Boolean);
      if (!words.every((word) => haystack.includes(word))) return false;
    }

    // Type filter
    if (toolType !== 'all' && tool.toolType !== toolType) return false;

    // Journey filter
    if (isFilterVisible('journey') && activeJourneys.length > 0 && tool.journeys) {
      if (!activeJourneys.some(j => tool.journeys.includes(j))) return false;
    }

    // Role filter
    if (isFilterVisible('role') && activeRoles.length > 0 && tool.roles) {
      if (!activeRoles.some(r => tool.roles.includes(r))) return false;
    }

    // Tag filter
    if (isFilterVisible('tag') && activeTags.length > 0 && tool.tags) {
      if (!activeTags.some(t => tool.tags.includes(t))) return false;
    }

    // Capability filter
    if (isFilterVisible('capability') && activeCapabilities.length > 0) {
      const hasCapability = activeCapabilities.some(capability => {
        const capLower = capability.toLowerCase();

        // First check dedicated capabilities field (preferred)
        if (tool.capabilities && tool.capabilities.some(cap => cap.toLowerCase() === capLower)) {
          return true;
        }

        // Fallback: Check in tags for backward compatibility
        if (tool.tags && tool.tags.some(tag => tag.toLowerCase().includes(capLower))) {
          return true;
        }

        // Fallback: Check in whatItDoes (features list)
        if (tool.whatItDoes && tool.whatItDoes.some(feature =>
          feature.toLowerCase().includes(capLower)
        )) {
          return true;
        }

        // Fallback: Check in title/description
        if (tool.title && tool.title.toLowerCase().includes(capLower)) {
          return true;
        }

        if (tool.description && tool.description.toLowerCase().includes(capLower)) {
          return true;
        }

        return false;
      });

      if (!hasCapability) return false;
    }

    // Persona filter
    if (isFilterVisible('persona') && activePersonas.length > 0 && tool.personas) {
      if (!activePersonas.some(p => tool.personas.includes(p))) return false;
    }

    return true;
  });

  // Helper function to calculate recommendation score
  const calculateRecommendationScore = (tool) => {
    let score = 0;

    // Factor 1: Popularity (40% weight)
    score += (tool.usageCount || 0) * 0.4;

    // Factor 2: Recent activity (30% weight)
    score += (tool.sessions7d || 0) * 3; // 3x multiplier for weekly sessions

    // Factor 3: User preference match (30% weight)
    // Match with active filters
    if (activeJourneys.length > 0 && tool.journeys) {
      const matchCount = activeJourneys.filter(j => tool.journeys.includes(j)).length;
      score += matchCount * 10;
    }
    if (activeRoles.length > 0 && tool.roles) {
      const matchCount = activeRoles.filter(r => tool.roles.includes(r)).length;
      score += matchCount * 10;
    }
    if (activeTags.length > 0 && tool.tags) {
      const matchCount = activeTags.filter(t => tool.tags.includes(t)).length;
      score += matchCount * 5;
    }

    // Boost for favorited/pinned tools
    if (favorites.includes(tool.id)) score += 15;
    if (pins.includes(tool.id)) score += 10;

    return score;
  };

  // Sort tools
  const sortedTools = [...filteredTools].sort((a, b) => {
    // Give title matches priority over matches found only in other fields.
    // Count matched query words so titles covering more of the query rank first.
    const queryWords = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const titleMatchCount = (tool) => {
      const title = (tool.title || '').toLowerCase();
      return queryWords.reduce((count, word) => count + (title.includes(word) ? 1 : 0), 0);
    };
    const titlePriority = titleMatchCount(b) - titleMatchCount(a);
    if (titlePriority !== 0) return titlePriority;

    switch (sortBy) {
      case 'recommended':
        // Recommendation algorithm based on multiple factors
        const scoreA = calculateRecommendationScore(a);
        const scoreB = calculateRecommendationScore(b);
        return scoreB - scoreA;
      case 'recent': {
        // Recency comes from the same per-user "garage-recent-tools" list the
        // Simplified view's Recently Used section reads (set on tool launch) -
        // tools carry no lastUsed timestamp of their own.
        const recentToolIds = JSON.parse(localStorage.getItem('garage-recent-tools') || '[]');
        const indexA = recentToolIds.indexOf(a.id);
        const indexB = recentToolIds.indexOf(b.id);
        const rankA = indexA === -1 ? Infinity : indexA;
        const rankB = indexB === -1 ? Infinity : indexB;
        return rankA - rankB;
      }
      case 'popular':
        return (b.usageCount || 0) - (a.usageCount || 0);
      case 'new':
        return new Date(b.createdAt) - new Date(a.createdAt);
      case 'az':
        return (a.title || '').localeCompare(b.title || '');
      default:
        return 0;
    }
  });

  // Toggle section collapse/expand
  const toggleSection = (sectionKey) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionKey)) {
        newSet.delete(sectionKey);
      } else {
        newSet.add(sectionKey);
      }
      return newSet;
    });
  };

  // Expand all sections
  const expandAllSections = () => {
    setCollapsedSections(new Set());
  };

  // Collapse all sections
  const collapseAllSections = () => {
    const allKeys = groupedTools.map(g => g.key);
    setCollapsedSections(new Set(allKeys));
  };

  // Group tools based on groupBy setting
  const groupedTools = React.useMemo(() => {
    if (groupBy === 'none') {
      return [{ key: 'all', label: null, tools: sortedTools }];
    }

    const groups = {};
    const ungrouped = [];

    sortedTools.forEach(tool => {
      let grouped = false;

      if (groupBy === 'journey') {
        // Group by sales stages/journeys
        if (tool.journeys && tool.journeys.length > 0) {
          tool.journeys.forEach(journey => {
            if (!groups[journey]) {
              groups[journey] = { key: journey, label: journey, tools: [] };
            }
            groups[journey].tools.push(tool);
          });
          grouped = true;
        }
      } else if (groupBy === 'role') {
        // Group by roles
        if (tool.roles && tool.roles.length > 0) {
          tool.roles.forEach(role => {
            if (!groups[role]) {
              groups[role] = { key: role, label: role, tools: [] };
            }
            groups[role].tools.push(tool);
          });
          grouped = true;
        }
      } else if (groupBy === 'type') {
        // Group by tool type (App vs Gem)
        const type = tool.toolType || 'Other';
        if (!groups[type]) {
          groups[type] = {
            key: type,
            label: type === 'App' ? '📱 Apps' : type === 'Gem' ? '💎 Gems' : 'Other',
            tools: []
          };
        }
        groups[type].tools.push(tool);
        grouped = true;
      }

      if (!grouped) {
        ungrouped.push(tool);
      }
    });

    // Convert groups object to array and sort by tool count (descending)
    let result = Object.values(groups).sort((a, b) => b.tools.length - a.tools.length);

    // Add ungrouped section if there are ungrouped tools
    if (ungrouped.length > 0) {
      const ungroupedLabel = groupBy === 'journey' ? 'Not stage-specific'
                           : groupBy === 'role' ? 'Any role'
                           : 'Other';
      result.push({ key: '_ungrouped', label: ungroupedLabel, tools: ungrouped });
    }

    return result;
  }, [sortedTools, groupBy]);

  // Get active filter pills
  const getActiveFilters = () => {
    const filters = [];
    activeJourneys.forEach(j => filters.push({ facet: 'journey', value: j, label: j }));
    activeRoles.forEach(r => filters.push({
      facet: 'role',
      value: r,
      label: roleOptions.find(o => o.value === r)?.label || r,
    }));
    activeTags.forEach(t => filters.push({ facet: 'tag', value: t, label: t }));
    activeCapabilities.forEach(c => filters.push({ facet: 'capability', value: c, label: c.charAt(0).toUpperCase() + c.slice(1) }));
    activePersonas.forEach(p => filters.push({ facet: 'persona', value: p, label: p }));
    return filters;
  };

  const activeFilters = getActiveFilters();

  // Calculate stats for advanced view
  const stats = {
    total: tools.length,
    apps: tools.filter(t => t.toolType === 'App').length,
    gems: tools.filter(t => t.toolType === 'Gem').length,
    usedToday: tools.filter(t => t.sessions7d && t.sessions7d > 0).length,
    forSale: tools.filter(t => t.status === 'published').length,
    needsReview: tools.filter(t => t.status === 'pending' || t.status === 'in_review').length
  };

  // Get popular tools (top 6 by sessions7d)
  const popularTools = [...tools]
    .filter(t => t.sessions7d && t.sessions7d > 0)
    .sort((a, b) => (b.sessions7d || 0) - (a.sessions7d || 0))
    .slice(0, 6);

  // Get recently used tools (from localStorage or API)
  const getRecentlyUsedTools = () => {
    try {
      const recentToolIds = JSON.parse(localStorage.getItem('garage-recent-tools') || '[]');
      return recentToolIds
        .map(id => tools.find(t => t.id === id))
        .filter(Boolean)
        .slice(0, 10);
    } catch {
      return [];
    }
  };

  // Get recommended tools based on active personas
  const getRecommendedTools = () => {
    if (activePersonas.length === 0) {
      // If no personas selected, recommend based on popular tools
      return [...tools]
        .sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0))
        .slice(0, 10);
    }

    // Filter tools matching active personas
    return tools
      .filter(tool => tool.personas && activePersonas.some(p => tool.personas.includes(p)))
      .sort((a, b) => calculateRecommendationScore(b) - calculateRecommendationScore(a))
      .slice(0, 10);
  };

  // Reshuffle on each expansion, keeping suggestions stable while the section is open.
  const isTryNewExpanded = !collapsedSections.has('try-new');
  const trySomethingNewTools = useMemo(() => {
    if (usageHistory.email !== userEmail || !usageHistory.loaded) return [];
    const usedIds = new Set(usageHistory.ids);
    const unusedTools = tools.filter((tool) => !usedIds.has(tool.id));
    // Keep the section/count available while collapsed; sample when it opens.
    if (!isTryNewExpanded) return unusedTools.slice(0, 5);
    for (let index = unusedTools.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [unusedTools[index], unusedTools[randomIndex]] = [unusedTools[randomIndex], unusedTools[index]];
    }
    return unusedTools.slice(0, 5);
  }, [tools, usageHistory, userEmail, isTryNewExpanded]);

  // Get bookmarked tools
  const getBookmarkedTools = () => {
    return tools.filter(tool => pins.includes(tool.id)).slice(0, 10);
  };

  const recentlyUsedTools = getRecentlyUsedTools().slice(0, 5);
  const recommendedTools = getRecommendedTools().slice(0, 5);
  const bookmarkedTools = getBookmarkedTools().slice(0, 5);

  return (
    <div className="garage-content">
      {!selectedTool ? (
        <>
          {/* View Mode Toggle */}
          <div className="garage-view-mode-toggle">
            <button
              className={`garage-mode-btn ${filterMode === 'simplified' ? 'active' : ''}`}
              onClick={() => setFilterMode('simplified')}
            >
              <Flame size={16} />
              Simplified
            </button>
            <button
              className={`garage-mode-btn ${filterMode === 'advanced' ? 'active' : ''}`}
              onClick={() => setFilterMode('advanced')}
            >
              <Settings size={16} />
              Advanced
            </button>
          </div>

          {/* Preference Applied Indicator - Shows in BOTH views */}
          {preferencesApplied && preferenceSource === 'onboarding' && (
            <div className="garage-preference-banner">
              <div className="garage-preference-banner-content">
                <Sparkles size={16} />
                <span>
                  Showing tools personalized for you based on your preferences.
                </span>
              </div>
              <button
                className="garage-preference-banner-clear"
                onClick={clearAllFilters}
                title="Clear all filters and show all tools"
              >
                Show all tools
              </button>
            </div>
          )}

          {filterMode === 'simplified' ? (
            <>
              {/* Simplified View - Two Column Layout */}
              <div className="garage-simplified-layout">
                <div className="garage-simplified-main">
                  {/* Simplified View */}
                  <div className="garage-page-hero">
                <div>
                  <h1 className="garage-page-title">Welcome back, {userName || 'there'}</h1>
                  <p className="garage-page-subtitle">Discover AI tools used across RingCentral</p>
                </div>
                <div className="garage-hero-actions">
                  <button
                    className="garage-btn garage-btn-secondary"
                    onClick={() => setShowTour(true)}
                    title="Take a quick tour"
                  >
                    <Sparkles size={16} />
                    Help
                  </button>
                  <button className="garage-btn garage-btn-primary" onClick={() => onNavigate('request-flow')}>
                    <Plus size={16} />
                    Submit
                  </button>
                </div>
              </div>

              {/* Search Bar + View Controls - Single Row */}
              <div className="garage-search-controls-row" style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
                {/* Simple Search - Smaller Width */}
                <div className="garage-simple-search" data-tour="discover-search" style={{ maxWidth: '400px', flex: '0 0 400px' }}>
                  <Search size={20} />
                  <input
                    type="text"
                    placeholder="Search by name or problem..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button className="garage-search-clear-btn" onClick={() => setSearchQuery('')}>
                      <X size={16} />
                    </button>
                  )}
                </div>

                {/* View Controls */}
                <div className="garage-view-controls" style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: 1 }}>
                {/* View mode toggle - Only Grid and List for Simple view */}
                <div className="garage-view-toggle">
                  <button
                    className={viewMode === 'grid' ? 'active' : ''}
                    onClick={() => setViewMode('grid')}
                    title="Grid view"
                  >
                    <Grid size={16} />
                  </button>
                  <button
                    className={viewMode === 'list' ? 'active' : ''}
                    onClick={() => setViewMode('list')}
                    title="List view"
                  >
                    <List size={16} />
                  </button>
                </div>

                {/* Type filter */}
                <div className="garage-type-filter" style={{ display: 'flex', gap: '4px', background: 'var(--garage-surface-2)', borderRadius: '999px', padding: '4px' }}>
                  <button
                    className={`garage-type-btn ${toolType === 'all' ? 'active' : ''}`}
                    onClick={() => setToolType('all')}
                    style={{
                      padding: '6px 16px',
                      background: toolType === 'all' ? 'var(--garage-orange)' : 'transparent',
                      color: toolType === 'all' ? 'white' : 'var(--garage-text-2)',
                      border: 'none',
                      borderRadius: '999px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    All
                  </button>
                  <button
                    className={`garage-type-btn ${toolType === 'App' ? 'active' : ''}`}
                    onClick={() => setToolType('App')}
                    style={{
                      padding: '6px 16px',
                      background: toolType === 'App' ? 'var(--garage-orange)' : 'transparent',
                      color: toolType === 'App' ? 'white' : 'var(--garage-text-2)',
                      border: 'none',
                      borderRadius: '999px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    Apps
                  </button>
                  <button
                    className={`garage-type-btn ${toolType === 'Gem' ? 'active' : ''}`}
                    onClick={() => setToolType('Gem')}
                    style={{
                      padding: '6px 16px',
                      background: toolType === 'Gem' ? 'var(--garage-orange)' : 'transparent',
                      color: toolType === 'Gem' ? 'white' : 'var(--garage-text-2)',
                      border: 'none',
                      borderRadius: '999px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    Gems
                  </button>
                </div>
                </div>
              </div>

              {/* Simplified view exposes only the persona-configured Stage facet. */}
              {isFilterVisible('journey') && (
                <div className="garage-stage-filter" style={{ marginBottom: '24px' }}>
                <div className="garage-stage-bubbles" style={{ display: 'flex', gap: '8px', overflowX: 'auto', padding: '4px' }}>
                  {/* All Stages button */}
                  <button
                    className={`garage-stage-bubble ${activeJourneys.length === 0 ? 'active' : ''}`}
                    onClick={() => setActiveJourneys([])}
                    style={{
                      padding: '8px 16px',
                      border: '2px solid var(--garage-border)',
                      borderRadius: '999px',
                      background: activeJourneys.length === 0 ? 'var(--garage-orange)' : 'var(--garage-surface)',
                      color: activeJourneys.length === 0 ? 'white' : 'var(--garage-text-1)',
                      borderColor: activeJourneys.length === 0 ? 'var(--garage-orange)' : 'var(--garage-border)',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      fontSize: '13px',
                      fontWeight: '600',
                      transition: 'all 0.2s'
                    }}
                  >
                    All Stages
                  </button>

                  {/* Individual stage bubbles - Dynamic from journeyOptions */}
                  {journeyOptions.map(journey => (
                    <button
                      key={journey}
                      className={`garage-stage-bubble ${activeJourneys.includes(journey) ? 'active' : ''}`}
                      onClick={() => toggleFacet('journey', journey)}
                      style={{
                        padding: '8px 16px',
                        border: '2px solid var(--garage-border)',
                        borderRadius: '999px',
                        background: activeJourneys.includes(journey) ? 'var(--garage-orange)' : 'var(--garage-surface)',
                        color: activeJourneys.includes(journey) ? 'white' : 'var(--garage-text-1)',
                        borderColor: activeJourneys.includes(journey) ? 'var(--garage-orange)' : 'var(--garage-border)',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        fontSize: '13px',
                        fontWeight: '600',
                        transition: 'all 0.2s'
                      }}
                    >
                      {journey}
                    </button>
                  ))}
                </div>
              </div>
              )}

              {/* Most Popular This Week */}
              {popularTools.length > 0 && !searchQuery && (
                <section className="garage-popular-section">
                  <div className="garage-section-header">
                    <Flame size={18} />
                    <h2>Most popular this week</h2>
                  </div>
                  <div className="garage-popular-tools">
                    {popularTools.map(tool => (
                      <div key={tool.id} className="garage-popular-card" onClick={() => handleToolClick(tool)}>
                        <div className="garage-popular-card-icon" style={{ background: tool.color || '#0684BC' }}>
                          {tool.icon || '🔧'}
                        </div>
                        <div className="garage-popular-card-info">
                          <div className="garage-popular-card-badges">
                            {tool.toolType === 'App' && <span className="garage-badge-app">APP</span>}
                            {tool.toolType === 'Gem' && <span className="garage-badge-gem">GEM</span>}
                          </div>
                          <h3>{tool.title}</h3>
                          <p>{tool.description}</p>
                        </div>
                        <button
                          className="garage-popular-card-action"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onOpenTool) onOpenTool(tool.id);
                          }}
                          title="View details"
                        >
                          <ExternalLink size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* All Tools Section - Always Expanded */}
              <section className="garage-all-tools-section">
                <div className="garage-section-header-simple" style={{ marginBottom: '20px' }}>
                  <h2 style={{ margin: 0 }}>All Tools</h2>
                  <span className="garage-tools-count">{sortedTools.length} tools</span>
                </div>
                {loading ? (
                  <div className="garage-loading">Loading tools...</div>
                ) : sortedTools.length === 0 ? (
                  <div className="garage-empty">
                    <div className="garage-empty-icon">🔍</div>
                    <h3>No tools found</h3>
                    <p>Try a different search term</p>
                  </div>
                ) : (
                  <div className={viewMode === 'grid' ? 'garage-tools-grid' : 'garage-tools-list'} data-tour="discover-tools">
                    {sortedTools.map(tool => (
                      <div
                        key={tool.id}
                        className={`garage-tool-card ${getToolAccessButtonState(accessStates, tool.id) !== 'launch' ? 'access-locked' : ''}`}
                        onClick={() => handleCardPrimaryAction(tool)}
                      >
                        {/* Tool Image/Thumbnail */}
                        {tool.imageUrl ? (
                          <div className={`garage-tool-thumbnail ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}>
                            <img src={tool.imageUrl} alt={tool.title} />
                          </div>
                        ) : (
                          <div className={`garage-tool-thumbnail garage-tool-thumbnail-placeholder ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}>
                            <div className="garage-tool-placeholder-icon">{tool.toolType === 'Gem' ? '✨' : '📱'}</div>
                          </div>
                        )}

                        {/* Collection Button (Star) */}
                        <button
                          className={`garage-tool-favorite ${favorites.includes(tool.id) ? 'favorited' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(tool.id);
                          }}
                          title={favorites.includes(tool.id) ? 'Remove from collection' : 'Add to collection'}
                        >
                          <Star size={16} fill={favorites.includes(tool.id) ? 'currentColor' : 'none'} />
                        </button>

                        {/* Bookmark Button */}
                        <button
                          className={`garage-tool-pin ${pins.includes(tool.id) ? 'pinned' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePin(tool.id);
                          }}
                          title={pins.includes(tool.id) ? 'Remove bookmark' : 'Bookmark this tool'}
                        >
                          <Bookmark size={16} fill={pins.includes(tool.id) ? 'currentColor' : 'none'} />
                        </button>

                        {/* Share Button */}
                        <button
                          className={`garage-tool-share ${sharedToolId === tool.id ? 'shared' : ''}`}
                          onClick={(e) => handleShareTool(e, tool)}
                          title={sharedToolId === tool.id ? 'Link copied!' : 'Share this tool'}
                        >
                          {sharedToolId === tool.id ? (
                            <Check size={16} />
                          ) : (
                            <Share2 size={16} />
                          )}
                        </button>

                        {/* Tool Content */}
                        <div className="garage-tool-content">
                          {viewMode === 'list' ? (
                            <>
                              {/* List View Layout */}
                              <div className="garage-tool-info">
                                <div className="garage-tool-badge-row">
                                  <span className={`garage-tool-type-badge ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}>
                                    {(tool.persona || 'GENERAL').toUpperCase()} · {getToolTypeBadgeSuffix(tool)}
                                  </span>
                                </div>
                                <h3 className="garage-tool-title">{tool.title}</h3>
                                <ToolCardDescriptionOrSteps tool={tool} maxSteps={gridSize === 'small' ? 2 : 3} />
                                {(tool.makerName || tool.makerEmail || tool.createdBy) && (
                                  <p className="garage-tool-creator">
                                    By <strong>{tool.makerName || tool.makerEmail || tool.createdBy}</strong>
                                  </p>
                                )}
                                {accessStates[tool.id]?.rejectionReason && (
                                  <p className="garage-access-card-rejection">
                                    Rejected: {accessStates[tool.id].rejectionReason}
                                  </p>
                                )}
                              </div>

                              {/* Actions for list view */}
                              {getToolAccessButtonState(accessStates, tool.id) !== 'launch' ? (
                                <div className="garage-tool-actions">
                                  <button
                                    className="garage-tool-btn garage-tool-btn-secondary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (onOpenTool) onOpenTool(tool.id);
                                    }}
                                  >
                                    <ExternalLink size={14} />
                                    Details
                                  </button>
                                  <ToolAccessPrimaryButton
                                    tool={tool}
                                    accessStates={accessStates}
                                    onLaunch={handleLaunchTool}
                                    onRequestAccess={setRequestAccessTool}
                                  />
                                </div>
                              ) : (
                                <ToolCardActions
                                  tool={tool}
                                  userEmail={userEmail}
                                  garageAPI={garageAPI}
                                  onOpenTool={onOpenTool}
                                  handleLaunchTool={handleLaunchTool}
                                  showStepsPreview={false}
                                />
                              )}
                            </>
                          ) : (
                            <>
                              {/* Grid View Layout */}
                              <div className="garage-tool-badge-row">
                                <span className={`garage-tool-type-badge ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}>
                                  {(tool.persona || 'GENERAL').toUpperCase()} · {getToolTypeBadgeSuffix(tool)}
                                </span>
                              </div>
                              <h3 className="garage-tool-title">{tool.title}</h3>
                              <ToolCardDescriptionOrSteps tool={tool} maxSteps={gridSize === 'small' ? 2 : 3} />

                              {(tool.makerName || tool.makerEmail || tool.createdBy) && (
                                <p className="garage-tool-creator">
                                  By <strong>{tool.makerName || tool.makerEmail || tool.createdBy}</strong>
                                </p>
                              )}
                              {accessStates[tool.id]?.rejectionReason && (
                                <p className="garage-access-card-rejection">
                                  Rejected: {accessStates[tool.id].rejectionReason}
                                </p>
                              )}

                              {/* Actions for grid view */}
                              {getToolAccessButtonState(accessStates, tool.id) !== 'launch' ? (
                                <div className="garage-tool-actions">
                                  <button
                                    className="garage-tool-btn garage-tool-btn-secondary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (onOpenTool) onOpenTool(tool.id);
                                    }}
                                  >
                                    Details
                                  </button>
                                  <ToolAccessPrimaryButton
                                    tool={tool}
                                    accessStates={accessStates}
                                    onLaunch={handleLaunchTool}
                                    onRequestAccess={setRequestAccessTool}
                                  />
                                </div>
                              ) : (
                                <ToolCardActions
                                  tool={tool}
                                  userEmail={userEmail}
                                  garageAPI={garageAPI}
                                  onOpenTool={onOpenTool}
                                  handleLaunchTool={handleLaunchTool}
                                  showStepsPreview={false}
                                />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>

            {/* Right Sidebar - Pill Sections (Vertical Layout) */}
            <div className="garage-simplified-sidebar">
              {!searchQuery && (
                <>
                  {/* Recently Used */}
                  {recentlyUsedTools.length > 0 && (
                    <section className="garage-pill-section">
                      <div
                        className="garage-pill-section-header"
                        onClick={() => toggleSection('recently-used')}
                      >
                        <div className="garage-pill-section-title">
                          <div className="garage-pill-section-icon">
                            <TrendingUp size={18} />
                          </div>
                          <h2>
                            Recently Used
                            <ChevronDown
                              size={18}
                              className={`garage-pill-section-toggle ${collapsedSections.has('recently-used') ? 'collapsed' : ''}`}
                            />
                          </h2>
                        </div>
                        <span className="garage-pill-section-count">{recentlyUsedTools.length} tools</span>
                      </div>
                      {!collapsedSections.has('recently-used') && (
                        <div className="garage-pill-cards-scroll">
                          {recentlyUsedTools.map(tool => (
                            <div
                              key={tool.id}
                              className="garage-pill-card"
                              onClick={() => handleToolClick(tool)}
                            >
                              <div className="garage-pill-card-icon">
                                {tool.imageUrl ? (
                                  <img src={tool.imageUrl} alt={tool.title} />
                                ) : (
                                  <div className="garage-pill-card-icon-placeholder" style={{ background: tool.color || '#0684BC' }}>
                                    {tool.icon || (tool.toolType === 'Gem' ? '✨' : '📱')}
                                  </div>
                                )}
                              </div>
                              <div className="garage-pill-card-info">
                                <div className={`garage-pill-card-badge ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}>
                                  {getToolTypeBadgeSuffix(tool)}
                                </div>
                                <h3 className="garage-pill-card-title">{tool.title}</h3>
                                <p className="garage-pill-card-description">{tool.description}</p>
                              </div>
                              <div className="garage-pill-card-footer">
                                <button
                                  className="garage-pill-card-action-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onOpenTool) onOpenTool(tool.id);
                                  }}
                                  title="View details"
                                >
                                  <ExternalLink size={12} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  )}

                  {/* Recommended */}
                  {recommendedTools.length > 0 && (
                    <section className="garage-pill-section">
                      <div
                        className="garage-pill-section-header"
                        onClick={() => toggleSection('recommended')}
                      >
                        <div className="garage-pill-section-title">
                          <div className="garage-pill-section-icon">
                            <Star size={18} />
                          </div>
                          <h2>
                            Recommended for You
                            <ChevronDown
                              size={18}
                              className={`garage-pill-section-toggle ${collapsedSections.has('recommended') ? 'collapsed' : ''}`}
                            />
                          </h2>
                        </div>
                        <span className="garage-pill-section-count">{recommendedTools.length} tools</span>
                      </div>
                      {!collapsedSections.has('recommended') && (
                        <div className="garage-pill-cards-scroll">
                          {recommendedTools.map(tool => (
                            <div
                              key={tool.id}
                              className="garage-pill-card"
                              onClick={() => handleToolClick(tool)}
                            >
                              <div className="garage-pill-card-icon">
                                {tool.imageUrl ? (
                                  <img src={tool.imageUrl} alt={tool.title} />
                                ) : (
                                  <div className="garage-pill-card-icon-placeholder" style={{ background: tool.color || '#0684BC' }}>
                                    {tool.icon || (tool.toolType === 'Gem' ? '✨' : '📱')}
                                  </div>
                                )}
                              </div>
                              <div className="garage-pill-card-info">
                                <div className={`garage-pill-card-badge ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}>
                                  {getToolTypeBadgeSuffix(tool)}
                                </div>
                                <h3 className="garage-pill-card-title">{tool.title}</h3>
                                <p className="garage-pill-card-description">{tool.description}</p>
                              </div>
                              <div className="garage-pill-card-footer">
                                <button
                                  className="garage-pill-card-action-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onOpenTool) onOpenTool(tool.id);
                                  }}
                                  title="View details"
                                >
                                  <ExternalLink size={12} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  )}

                  {/* Try Something New */}
                  {trySomethingNewTools.length > 0 && (
                    <section className="garage-pill-section">
                      <div
                        className="garage-pill-section-header"
                        onClick={() => toggleSection('try-new')}
                      >
                        <div className="garage-pill-section-title">
                          <div className="garage-pill-section-icon">
                            <Sparkles size={18} />
                          </div>
                          <h2>
                            Try Something New
                            <ChevronDown
                              size={18}
                              className={`garage-pill-section-toggle ${collapsedSections.has('try-new') ? 'collapsed' : ''}`}
                            />
                          </h2>
                        </div>
                        <span className="garage-pill-section-count">{trySomethingNewTools.length} tools</span>
                      </div>
                      {!collapsedSections.has('try-new') && (
                        <div className="garage-pill-cards-scroll">
                          {trySomethingNewTools.map(tool => (
                            <div
                              key={tool.id}
                              className="garage-pill-card"
                              onClick={() => handleToolClick(tool)}
                            >
                              <div className="garage-pill-card-icon">
                                {tool.imageUrl ? (
                                  <img src={tool.imageUrl} alt={tool.title} />
                                ) : (
                                  <div className="garage-pill-card-icon-placeholder" style={{ background: tool.color || '#0684BC' }}>
                                    {tool.icon || (tool.toolType === 'Gem' ? '✨' : '📱')}
                                  </div>
                                )}
                              </div>
                              <div className="garage-pill-card-info">
                                <div className={`garage-pill-card-badge ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}>
                                  {getToolTypeBadgeSuffix(tool)}
                                </div>
                                <h3 className="garage-pill-card-title">{tool.title}</h3>
                                <p className="garage-pill-card-description">{tool.description}</p>
                              </div>
                              <div className="garage-pill-card-footer">
                                <button
                                  className="garage-pill-card-action-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onOpenTool) onOpenTool(tool.id);
                                  }}
                                  title="View details"
                                >
                                  <ExternalLink size={12} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  )}

                  {/* Bookmarked Tools */}
                  {bookmarkedTools.length > 0 && (
                    <section className="garage-pill-section">
                      <div
                        className="garage-pill-section-header"
                        onClick={() => toggleSection('bookmarked')}
                      >
                        <div className="garage-pill-section-title">
                          <div className="garage-pill-section-icon">
                            <Bookmark size={18} />
                          </div>
                          <h2>
                            Bookmarked
                            <ChevronDown
                              size={18}
                              className={`garage-pill-section-toggle ${collapsedSections.has('bookmarked') ? 'collapsed' : ''}`}
                            />
                          </h2>
                        </div>
                        <span className="garage-pill-section-count">{bookmarkedTools.length} tools</span>
                      </div>
                      {!collapsedSections.has('bookmarked') && (
                        <div className="garage-pill-cards-scroll">
                          {bookmarkedTools.map(tool => (
                            <div
                              key={tool.id}
                              className="garage-pill-card"
                              onClick={() => handleToolClick(tool)}
                            >
                              <div className="garage-pill-card-icon">
                                {tool.imageUrl ? (
                                  <img src={tool.imageUrl} alt={tool.title} />
                                ) : (
                                  <div className="garage-pill-card-icon-placeholder" style={{ background: tool.color || '#0684BC' }}>
                                    {tool.icon || (tool.toolType === 'Gem' ? '✨' : '📱')}
                                  </div>
                                )}
                              </div>
                              <div className="garage-pill-card-info">
                                <div className={`garage-pill-card-badge ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}>
                                  {getToolTypeBadgeSuffix(tool)}
                                </div>
                                <h3 className="garage-pill-card-title">{tool.title}</h3>
                                <p className="garage-pill-card-description">{tool.description}</p>
                              </div>
                              <div className="garage-pill-card-footer">
                                <button
                                  className="garage-pill-card-action-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onOpenTool) onOpenTool(tool.id);
                                  }}
                                  title="View details"
                                >
                                  <ExternalLink size={12} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  )}
                </>
              )}
            </div>
          </div>
            </>
          ) : (
            <>
              {/* Advanced View */}
              {/* Hero Section with Actions */}
              <div className="garage-page-hero">
                <div>
                  <h1 className="garage-page-title">AI Garage</h1>
                  <p className="garage-page-subtitle">Discover and launch AI tools</p>
                </div>
                <div className="garage-hero-actions">
                  <button
                    className="garage-btn garage-btn-secondary"
                    onClick={() => setShowTour(true)}
                    title="Take a quick tour"
                  >
                    <Sparkles size={16} />
                    Help
                  </button>
                  <button className="garage-btn garage-btn-primary" onClick={() => onNavigate('request-flow')}>
                    <Plus size={16} />
                    Submit
                  </button>
                </div>
              </div>

          {/* Advanced Filter Bar */}
          <div className="garage-filter-bar">
        {/* Search */}
        <div className="garage-filter-search" data-tour="discover-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search by name or problem..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="garage-filter-clear-btn" onClick={() => setSearchQuery('')}>
              <X size={13} />
            </button>
          )}
        </div>

        {visibleDiscoverFilters.length > 0 && <div className="garage-filter-divider"></div>}

        {/* Facet Dropdowns - now using dynamic options */}
        {isFilterVisible('journey') && (
          <FacetDropdown
            facet="journey"
            label="Stage"
            options={journeyOptions}
            activeValues={activeJourneys}
            onToggle={toggleFacet}
          />
        )}
        {isFilterVisible('role') && (
          <FacetDropdown
            facet="role"
            label="Role"
            options={roleOptions}
            activeValues={activeRoles}
            onToggle={toggleFacet}
          />
        )}
        {isFilterVisible('tag') && (
          <FacetDropdown
            facet="tag"
            label="Tag"
            options={tagOptions}
            activeValues={activeTags}
            onToggle={toggleFacet}
          />
        )}
        {isFilterVisible('capability') && (
          <FacetDropdown
            facet="capability"
            label="Capability"
            options={capabilityOptions}
            activeValues={activeCapabilities}
            onToggle={toggleFacet}
          />
        )}
        {isFilterVisible('persona') && (
          <FacetDropdown
            facet="persona"
            label="Persona"
            options={personaOptions}
            activeValues={activePersonas}
            onToggle={toggleFacet}
          />
        )}

        {visibleDiscoverFilters.length > 0 && <span className="garage-filter-divider"></span>}

        {/* Group By */}
        <select
          className="garage-filter-select"
          data-tour="discover-grouping"
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
        >
          {GROUPING_OPTIONS.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        {/* Sort */}
        <select
          className="garage-filter-select"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          {SORT_OPTIONS.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        {/* View Toggle */}
        <div className="garage-view-toggle">
          <button
            className={viewMode === 'grid' ? 'active' : ''}
            onClick={() => setViewMode('grid')}
            title="Grid view"
          >
            <Grid size={14} />
          </button>
          <button
            className={viewMode === 'list' ? 'active' : ''}
            onClick={() => setViewMode('list')}
            title="List view"
          >
            <List size={14} />
          </button>
        </div>

        {/* Grid Size Selector - Only show when in grid view */}
        {viewMode === 'grid' && (
          <div className="garage-size-selector">
            <span className="garage-size-selector-label">Size</span>
            <div className="garage-size-selector-buttons">
              <button
                className={`garage-size-btn ${gridSize === 'small' ? 'active' : ''}`}
                onClick={() => setGridSize('small')}
                title="Small tiles"
              >
                S
              </button>
              <button
                className={`garage-size-btn ${gridSize === 'medium' ? 'active' : ''}`}
                onClick={() => setGridSize('medium')}
                title="Medium tiles"
              >
                M
              </button>
              <button
                className={`garage-size-btn ${gridSize === 'large' ? 'active' : ''}`}
                onClick={() => setGridSize('large')}
                title="Large tiles"
              >
                L
              </button>
            </div>
          </div>
        )}

        {/* Matrix View Button */}
        <button
          className={`garage-matrix-btn ${viewMode === 'matrix' ? 'active' : ''}`}
          onClick={() => setViewMode(viewMode === 'matrix' ? 'grid' : 'matrix')}
          title="Matrix view"
        >
          <Table size={14} />
          Matrix
        </button>

        <span className="garage-filter-divider"></span>

        {/* Type Filter */}
        <div className="garage-type-toggle">
          <button
            className={toolType === 'all' ? 'active' : ''}
            onClick={() => setToolType('all')}
          >
            All
          </button>
          <button
            className={toolType === 'App' ? 'active' : ''}
            onClick={() => setToolType('App')}
          >
            Apps
          </button>
          <button
            className={toolType === 'Gem' ? 'active' : ''}
            onClick={() => setToolType('Gem')}
          >
            Gems
          </button>
        </div>
      </div>

      {/* Active Filter Pills */}
      {(activeFilters.length > 0 || searchQuery) && (
        <div className="garage-active-filters">
          {activeFilters.map((filter, idx) => (
            <span key={idx} className="garage-active-filter-pill">
              {filter.label}
              <button onClick={() => removeFilter(filter.facet, filter.value)} title="Remove">
                <X size={10} />
              </button>
            </span>
          ))}
          {searchQuery && (
            <span className="garage-active-filter-pill">
              "{searchQuery}"
              <button onClick={() => setSearchQuery('')} title="Clear search">
                <X size={10} />
              </button>
            </span>
          )}
          <button className="garage-filter-clear" onClick={clearAllFilters}>
            Clear all
          </button>
          <span className="garage-filter-count">
            {sortedTools.length} tools
          </span>
        </div>
      )}

      {/* Matrix View */}
      {viewMode === 'matrix' ? (
        <GarageMatrixView
          tools={sortedTools}
          onCellClick={handleMatrixCellClick}
          onShowDetails={onOpenTool}
          onLaunchTool={handleLaunchTool}
          journeys={journeyOptions}
          roles={roleOptions.map(r => r.value)}
          capabilities={capabilityOptions}
          personas={personaOptions}
        />
      ) : (
        <>
          {/* Tools Grid/List */}
          <div className="garage-tools-section">
            {loading ? (
              <GarageLoadingScreen message="Loading tools..." />
            ) : sortedTools.length === 0 ? (
              <div className="garage-empty">
                <div className="garage-empty-icon">📦</div>
                <h3>No tools found</h3>
                <p>Try adjusting your filters or search query</p>
                <button
                  className="garage-btn garage-btn-primary"
                  onClick={clearAllFilters}
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <>
                {/* Expand/Collapse All buttons (only show when grouped) */}
                {groupBy !== 'none' && groupedTools.length > 1 && (
                  <div className="garage-group-controls">
                    <button
                      className="garage-group-control-btn"
                      onClick={expandAllSections}
                      title="Expand all sections"
                    >
                      <ChevronDown size={14} />
                      Expand All
                    </button>
                    <button
                      className="garage-group-control-btn"
                      onClick={collapseAllSections}
                      title="Collapse all sections"
                    >
                      <ChevronUp size={14} />
                      Collapse All
                    </button>
                  </div>
                )}

                {/* Render grouped or flat tools */}
                {groupedTools.map(group => {
                  const isCollapsed = collapsedSections.has(group.key);

                  return (
                    <div key={group.key} className={`garage-group-section ${isCollapsed ? 'collapsed' : ''}`}>
                      {/* Group header (only show if grouped) */}
                      {group.label && (
                        <div
                          className="garage-group-header"
                          onClick={() => toggleSection(group.key)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="garage-group-header-left">
                            <ChevronDown
                              size={18}
                              className="garage-group-chevron"
                              style={{
                                transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease'
                              }}
                            />
                            <h3 className="garage-group-title">{group.label}</h3>
                          </div>
                          <span className="garage-group-count">{group.tools.length} {group.tools.length === 1 ? 'tool' : 'tools'}</span>
                        </div>
                      )}

                      {/* Tools grid/list for this group (collapsible) */}
                      {!isCollapsed && (
                        <div className={viewMode === 'grid' ? `garage-tools-grid size-${gridSize}` : 'garage-tools-list'} data-tour="discover-tools">
                          {group.tools.map(tool => (
                  <div
                    key={tool.id}
                    className={`garage-tool-card ${getToolAccessButtonState(accessStates, tool.id) !== 'launch' ? 'access-locked' : ''}`}
                    onClick={() => handleCardPrimaryAction(tool)}
                  >
                    {/* Tool Image/Thumbnail */}
                {tool.imageUrl ? (
                  <div className={`garage-tool-thumbnail ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}>
                    <img src={tool.imageUrl} alt={tool.title} />
                  </div>
                ) : (
                  <div className={`garage-tool-thumbnail garage-tool-thumbnail-placeholder ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}>
                    <div className="garage-tool-placeholder-icon">{tool.toolType === 'Gem' ? '✨' : '📱'}</div>
                  </div>
                )}

                {/* Collection Button (Star) */}
                <button
                  className={`garage-tool-favorite ${favorites.includes(tool.id) ? 'favorited' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(tool.id);
                  }}
                  title={favorites.includes(tool.id) ? 'Remove from collection' : 'Add to collection'}
                >
                  <Star size={16} fill={favorites.includes(tool.id) ? 'currentColor' : 'none'} />
                </button>

                {/* Bookmark Button */}
                <button
                  className={`garage-tool-pin ${pins.includes(tool.id) ? 'pinned' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePin(tool.id);
                  }}
                  title={pins.includes(tool.id) ? 'Remove bookmark' : 'Bookmark this tool'}
                >
                  <Bookmark size={16} fill={pins.includes(tool.id) ? 'currentColor' : 'none'} />
                </button>

                {/* Share Button */}
                <button
                  className={`garage-tool-share ${sharedToolId === tool.id ? 'shared' : ''}`}
                  onClick={(e) => handleShareTool(e, tool)}
                  title={sharedToolId === tool.id ? 'Link copied!' : 'Share this tool'}
                >
                  {sharedToolId === tool.id ? (
                    <Check size={16} />
                  ) : (
                    <Share2 size={16} />
                  )}
                </button>

                {/* Tool Content */}
                <div className="garage-tool-content">
                  {viewMode === 'list' ? (
                    <>
                      {/* List View Layout */}
                      <div className="garage-tool-info">
                        <div className="garage-tool-badge-row">
                          <span className={`garage-tool-type-badge ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}>
                            {(tool.persona || 'GENERAL').toUpperCase()} · {getToolTypeBadgeSuffix(tool)}
                          </span>
                        </div>
                        <h3 className="garage-tool-title">{tool.title}</h3>
                        <ToolCardDescriptionOrSteps tool={tool} maxSteps={gridSize === 'small' ? 2 : 3} />
                        {(tool.makerName || tool.makerEmail || tool.createdBy) && (
                          <p className="garage-tool-creator">
                            By <strong>{tool.makerName || tool.makerEmail || tool.createdBy}</strong>
                          </p>
                        )}
                        {accessStates[tool.id]?.rejectionReason && (
                          <p className="garage-access-card-rejection">
                            Rejected: {accessStates[tool.id].rejectionReason}
                          </p>
                        )}
                      </div>

                      {/* Actions for list view */}
                      {getToolAccessButtonState(accessStates, tool.id) !== 'launch' ? (
                        <div className="garage-tool-actions">
                          <button
                            className="garage-tool-btn garage-tool-btn-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onOpenTool) onOpenTool(tool.id);
                            }}
                          >
                            Details
                          </button>
                          <ToolAccessPrimaryButton
                            tool={tool}
                            accessStates={accessStates}
                            onLaunch={handleLaunchTool}
                            onRequestAccess={setRequestAccessTool}
                          />
                        </div>
                      ) : (
                        <ToolCardActions
                          tool={tool}
                          userEmail={userEmail}
                          garageAPI={garageAPI}
                          onOpenTool={onOpenTool}
                          handleLaunchTool={handleLaunchTool}
                          showStepsPreview={false}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {/* Grid View Layout */}
                      <div className="garage-tool-badge-row">
                        <span className={`garage-tool-type-badge ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}>
                          {(tool.persona || 'GENERAL').toUpperCase()} · {getToolTypeBadgeSuffix(tool)}
                        </span>
                      </div>
                      <h3 className="garage-tool-title">{tool.title}</h3>
                      <ToolCardDescriptionOrSteps tool={tool} maxSteps={gridSize === 'small' ? 2 : 3} />

                      {(tool.makerName || tool.makerEmail || tool.createdBy) && (
                        <p className="garage-tool-creator">
                          By <strong>{tool.makerName || tool.makerEmail || tool.createdBy}</strong>
                        </p>
                      )}
                      {accessStates[tool.id]?.rejectionReason && (
                        <p className="garage-access-card-rejection">
                          Rejected: {accessStates[tool.id].rejectionReason}
                        </p>
                      )}

                      {/* Tool Actions */}
                      {getToolAccessButtonState(accessStates, tool.id) !== 'launch' ? (
                        <div className="garage-tool-actions">
                          <button
                            className="garage-tool-btn garage-tool-btn-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onOpenTool) onOpenTool(tool.id);
                            }}
                          >
                            Details
                          </button>
                          <ToolAccessPrimaryButton
                            tool={tool}
                            accessStates={accessStates}
                            onLaunch={handleLaunchTool}
                            onRequestAccess={setRequestAccessTool}
                          />
                        </div>
                      ) : (
                        <ToolCardActions
                          tool={tool}
                          userEmail={userEmail}
                          garageAPI={garageAPI}
                          onOpenTool={onOpenTool}
                          handleLaunchTool={handleLaunchTool}
                          showStepsPreview={false}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </>
      )}
            </>
          )}
        </>
      ) : (
        /* Tool Detail View - Enhanced */
        <GarageToolDetailEnhanced
          toolId={selectedTool}
          userEmail={userEmail}
          userName={userName}
          onNavigate={onNavigate}
          onNavigateToMaker={onNavigateToMaker}
          onToolLaunch={onToolLaunch}
          onOpenRelatedTool={handleShowDetails}
          onRefreshPinnedTools={() => {
            loadPins();
            if (onRefreshPinnedTools) onRefreshPinnedTools();
          }}
        />
      )}

      {/* Helper Tour — only after onboarding is done */}
      {showTour && !selectedTool && !showOnboarding && (
        <TourGuide
          steps={discoverTourSteps}
          storageKey="tour-completed-discover"
          onComplete={() => setShowTour(false)}
          onSkip={() => setShowTour(false)}
        />
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Share Tool Modal */}
      {toolToShare && (
        <ShareToolModal
          isOpen={shareModalOpen}
          tool={toolToShare}
          userEmail={userEmail}
          onClose={() => {
            setShareModalOpen(false);
            setToolToShare(null);
          }}
        />
      )}

      <RequestAccessModal
        isOpen={!!requestAccessTool}
        tool={requestAccessTool}
        onConfirm={handleSubmitAccessRequest}
        onCancel={() => setRequestAccessTool(null)}
      />
    </div>
  );
};

export default GarageDiscoverView;
